
import { enhancedSecurityService } from './enhancedSecurityService';

export const inputValidationService = {
  // Validate and sanitize text inputs with context-aware rules
  validateTextInput(input: string, context: 'name' | 'description' | 'instruction' | 'general' = 'general'): string {
    if (!input) return '';

    const maxLengths = {
      name: 100,
      description: 500,
      instruction: 2000,
      general: 1000
    };

    const sanitized = enhancedSecurityService.sanitizeAndValidateInput(
      input, 
      context
    );

    // Apply length limits
    const maxLength = maxLengths[context];
    const truncated = sanitized.length > maxLength ? sanitized.slice(0, maxLength) : sanitized;

    // Additional context-specific validation
    switch (context) {
      case 'name':
        // Names should not contain excessive special characters
        return truncated.replace(/[^\w\s\-_().]/g, '');
      
      case 'description':
      case 'instruction':
        // Allow more characters for descriptions but still sanitize
        return truncated.replace(/[<>]/g, '');
      
      default:
        return truncated;
    }
  },

  // Validate email addresses
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  },

  // Validate numeric inputs
  validateNumericInput(value: any, min: number = 0, max: number = Number.MAX_SAFE_INTEGER): number | null {
    const num = Number(value);
    if (isNaN(num) || num < min || num > max) {
      return null;
    }
    return num;
  },

  // Validate and sanitize organization names
  validateOrganizationName(name: string): string {
    if (!name) return '';
    
    // Organization names have stricter rules
    const sanitized = this.validateTextInput(name, 'name');
    
    // Ensure minimum length and no purely numeric names
    if (sanitized.length < 2 || /^\d+$/.test(sanitized)) {
      throw new Error('Organization name must be at least 2 characters and cannot be purely numeric');
    }
    
    return sanitized;
  },

  // Validate file uploads (for future avatar uploads)
  validateFileUpload(file: File, allowedTypes: string[] = ['image/jpeg', 'image/png', 'image/webp']): boolean {
    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return false;
    }
    
    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      return false;
    }
    
    return true;
  },

  // Validate URL inputs
  validateUrl(url: string, allowedProtocols: string[] = ['http:', 'https:']): boolean {
    try {
      const parsedUrl = new URL(url);
      return allowedProtocols.includes(parsedUrl.protocol);
    } catch {
      return false;
    }
  }
};
